import PropTypes from 'prop-types';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { useParams } from 'react-router';
import * as Sentry from '@sentry/react';
import useFeedbackAttachments, { ACCEPT, MAX_FILES } from '@/hooks/useFeedbackAttachments';
import { useEmail, useName } from '@/stores/userStore';
import Tooltip from './Tooltip';

const FeedbackForm = ({ isOpen, onClose }) => {
  const { projectId, pathwayId } = useParams();
  const email = useEmail();
  const name = useName();
  const { files, fileError, setFileError, handleFilesSelected, removeFile, uploadFiles, resetAttachments } =
    useFeedbackAttachments();

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({
    defaultValues: {
      message: '',
      allowAccess: false,
    },
  });

  const message = useWatch({ control, name: 'message' });

  const onSubmit = async data => {
    try {
      const { message, allowAccess } = data;
      const fileUrls = await uploadFiles();

      Sentry.captureFeedback(
        {
          message,
          name: name || 'No Name',
          email: email || undefined,
        },
        {
          captureContext: {
            tags: {
              allow_project_access: allowAccess,
              ...(projectId && { project_id: projectId.toString() }),
              ...(pathwayId && { pathway_id: pathwayId.toString() }),
            },
            ...(fileUrls.length && { contexts: { attachments: { urls: fileUrls } } }),
          },
        },
      );

      reset();
      resetAttachments();
      onClose();
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      setFileError('Failed to upload attachments. Please try again.');
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      resetAttachments();
      onClose();
    }
  };

  return (
    <dialog className="modal" open={isOpen}>
      <div className="modal-box max-w-lg">
        <button
          type="button"
          className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          ✕
        </button>
        <h3 className="font-bold text-lg mb-4">Report a Bug</h3>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-control mb-4">
            <label htmlFor="feedback-message" className="label">
              <span className="label-text">Description (required)</span>
            </label>
            <textarea
              id="feedback-message"
              className={`textarea textarea-bordered h-32 ${errors.message ? 'textarea-error' : ''}`}
              placeholder="What's the bug? What did you expect?"
              {...register('message', {
                required: 'Description is required',
                validate: value => value.trim().length > 0 || 'Description cannot be empty',
              })}
              disabled={isSubmitting}
            />
            {errors.message && (
              <div className="label">
                <span className="label-text-alt text-error">{errors.message.message}</span>
              </div>
            )}
          </div>

          <div className="form-control mb-4">
            <label htmlFor="feedback-files" className="label">
              <span className="label-text">Attachments (optional)</span>
            </label>
            <input
              id="feedback-files"
              type="file"
              multiple
              accept={ACCEPT}
              className="file-input file-input-bordered file-input-sm w-full file:appearance-none file:border-0 file:border-none file:bg-secondary file:text-secondary-content file:normal-case"
              onChange={handleFilesSelected}
              disabled={isSubmitting || files.length >= MAX_FILES}
            />
            <div className="label">
              <span className="label-text-alt text-gray-500">
                Up to {MAX_FILES} files, 10 MB each. PDF, Word, Excel, CSV, JSON, TXT, PNG, JPG.
              </span>
            </div>
            {files.length > 0 && (
              <ul className="flex flex-col gap-1">
                {files.map((file, index) => (
                  <li key={`${file.name}-${index}`} className="flex items-center justify-between text-sm">
                    <span className="truncate">{file.name}</span>
                    <button
                      type="button"
                      className="btn btn-xs btn-ghost"
                      onClick={() => removeFile(index)}
                      disabled={isSubmitting}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {fileError && (
              <div className="label">
                <span className="label-text-alt text-error">{fileError}</span>
              </div>
            )}
          </div>

          {projectId && (
            <div className="form-control mb-6">
              <label className="label cursor-pointer justify-start gap-3">
                <Controller
                  name="allowAccess"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <input
                      type="checkbox"
                      className="checkbox checkbox-sm"
                      checked={value}
                      onChange={e => onChange(e.target.checked)}
                      disabled={isSubmitting}
                    />
                  )}
                />
                <div className="flex items-center gap-2">
                  <span className="label-text">Allow Sesame team to access this project for troubleshooting</span>
                  <Tooltip
                    label="Project Access"
                    text="This helps us resolve your issue faster by letting our support team view and modify the specific project you're reporting on. Access will be limited to troubleshooting this issue only and removed once resolved. Without this access, we'll need to request permission separately, which may delay the fix."
                  />
                </div>
              </label>
            </div>
          )}

          <div className="modal-action">
            <button type="button" className="btn btn-sm btn-secondary" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-sm btn-accent" disabled={isSubmitting || !message?.trim()}>
              {isSubmitting ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Submitting...
                </>
              ) : (
                'Submit'
              )}
            </button>
          </div>
        </form>
      </div>
      <form method="dialog" className="modal-backdrop" onClick={handleClose}>
        <button type="button">close</button>
      </form>
    </dialog>
  );
};

FeedbackForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default FeedbackForm;
